"use client";

import {
  ResourceType,
  WorkplaceRequestElement,
  WorkplaceRequestFieldValueInput,
  WorkplaceRequestLocationValue,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import {
  type WizardFooterRenderProps,
  WizardModal,
  type WizardStep
} from "@gettactic/components/src/overlay/modals/WizardModal";
import { Button } from "@gettactic/components/src/primitives/button";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import { isSubmittableFieldType } from "@gettactic/helpers/src/workplace-requests/fieldTraits";
import { isSubmittableRequestType } from "@gettactic/helpers/src/workplace-requests/requestTypeKind";
import { reservationFieldConfig } from "@gettactic/helpers/src/workplace-requests/reservationField";
import { toWorkplaceRequestFieldValue } from "@gettactic/helpers/src/workplace-requests/toWorkplaceRequestFieldValue";
import { useAuthenticated, useOfficesQuery } from "@gettactic/hooks";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { FieldValues } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useSubmittableWorkplaceRequestTypes } from "@/features/workplace-requests/hooks/useSubmittableWorkplaceRequestTypes";
import { useWorkplaceRequestCategories } from "@/features/workplace-requests/hooks/useWorkplaceRequestCategories";
import { useWorkplaceRequestMutations } from "@/features/workplace-requests/hooks/useWorkplaceRequestMutations";
import {
  type ReservationWhen,
  reservationValueFromWhen
} from "@/features/workplace-requests/lib/reservationWhen";
import { RequestConfirmation } from "./steps/RequestConfirmation";
import { RequestFormStep } from "./steps/RequestFormStep";
import { RequestInstructionsStep } from "./steps/RequestInstructionsStep";
import { RequestLocationField } from "./steps/RequestLocationField";
import { RequestTypePicker } from "./steps/RequestTypePicker";
import type { ReservationResourceContext } from "./steps/ReservationResourceAvailability";

/** Context passed when the wizard is opened for a specific map resource. */
export type NewRequestResourceContext = {
  id: string;
  /** Display name of the resource (e.g. "Desk 12"). */
  name: string;
  /** Office the resource belongs to; pre-fills "Filing this for". */
  officeId: string;
  /** Floor/area display name, when known (e.g. "Floor 4"). */
  floorName?: string;
  /** What kind of resource it is, for the resource's day under a reservation question. */
  type?: ResourceType;
};

/** Context passed when the wizard is opened from a pinned point on the office map. */
export type NewRequestMapPinContext = {
  officeId: string;
  officeName?: string;
  floorId?: string;
  floorName?: string;
  /** Map / SVG user-space coordinates. */
  x: number;
  y: number;
};

/**
 * A workplace request assembled by the wizard but not yet persisted. Used by the
 * reservation flow, which collects requests as drafts and saves them only when the
 * reservation itself is saved. `values` carries the raw react-hook-form state so the
 * wizard can re-hydrate every field (including dynamic ones) when the draft is edited.
 */
export interface CollectedWorkplaceRequestDraft {
  /** Stable client id used by the caller to track the draft in its list. */
  draftId: string;
  type: WorkplaceRequestTypeElement;
  title: string;
  description?: string;
  office_id: string;
  /** Ticket-level location for the request (resource, floor, or pinned point). */
  location?: WorkplaceRequestLocationValue;
  field_values: WorkplaceRequestFieldValueInput[];
  values: FieldValues;
}

interface NewRequestWizardProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onViewQueue?: () => void;
  /** When filing against a map resource, pre-fills office/resource and shows its context. */
  resource?: NewRequestResourceContext;
  /** When filing from a map pin, pre-fills office and attaches the map coordinates. */
  mapPin?: NewRequestMapPinContext;
  /** Links the created request to a reservation, when filed from one. */
  scheduleId?: string;
  /**
   * Collect mode: when provided, the wizard does NOT call the API. It validates and
   * returns the assembled draft to this callback, then closes. Used by the reservation
   * flow so requests are persisted together with the reservation.
   */
  onCollect?: (draft: CollectedWorkplaceRequestDraft) => void;
  /** Pre-fills the wizard to edit an existing collected draft (collect mode). */
  initialDraft?: CollectedWorkplaceRequestDraft;
  /**
   * Locks the office to a fixed value (shown but disabled). Used by the reservation flow
   * where the office is dictated by the reservation.
   */
  lockedOfficeId?: string;
  /**
   * Render the wizard scoped inside {@link portalContainer} (contained variant): the
   * backdrop dims only that container instead of the whole viewport. Used on the queue
   * page so the wizard opens within the surface card, matching the other detail panels.
   */
  contained?: boolean;
  /** DOM element to portal the wizard into. Used with {@link contained}. */
  portalContainer?: HTMLElement | null;
  /**
   * Restricts the type picker to these ids. Used when a resource is gated behind
   * specific request types: it cannot be reserved directly, only through one of
   * them. The office scope is pinned in this mode — widening it would offer types
   * the resource does not allow.
   */
  allowedTypeIds?: string[];
  /**
   * The days and hours the opener was showing — the map's date and time, or
   * the reservation a request is added to. A reservation question starts from
   * them instead of empty.
   */
  reservationWhen?: ReservationWhen;
}

const buildFieldValues = (
  type: WorkplaceRequestTypeElement,
  values: FieldValues
): WorkplaceRequestFieldValueInput[] => {
  const out: WorkplaceRequestFieldValueInput[] = [];
  for (const field of type.fields ?? []) {
    // An info block carries no answer, and the backend rejects the whole
    // request if one turns up in `field_values`.
    if (!isSubmittableFieldType(field.field_type)) {
      continue;
    }
    let value = toWorkplaceRequestFieldValue(
      field.field_type,
      values[field.id],
      reservationFieldConfig(field)
    );
    if (field.field_type === "number") {
      value = value === "" || value == null ? undefined : Number(value);
    }
    const isEmpty =
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0);
    if (isEmpty && field.field_type !== "toggle") {
      continue;
    }
    out.push({ field_id: field.id, value });
  }
  return out;
};

/**
 * The description the ticket should carry. Empty for a type that hides it — the
 * input is not rendered, but a value typed before the type was changed (or
 * restored from a draft) would otherwise still be submitted.
 */
const descriptionFor = (
  type: WorkplaceRequestTypeElement,
  values: FieldValues
): string =>
  type.hide_description ? "" : String(values.description || "").trim();

export const NewRequestWizard: React.FC<NewRequestWizardProps> = ({
  isOpen,
  onOpenChange,
  onViewQueue,
  resource,
  mapPin,
  scheduleId,
  onCollect,
  initialDraft,
  lockedOfficeId,
  contained,
  portalContainer,
  allowedTypeIds,
  reservationWhen
}) => {
  const isCollectMode = !!onCollect;
  const isEditMode = !!initialDraft;
  // Gated mode: the resource can only be booked through one of these types, so
  // the picker must never widen past them — see {@link allowedTypeIds}.
  const isGatedMode = !!allowedTypeIds?.length;
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const viewerName = authenticatedUser.user?.name || "you";
  const toast = useLoadingToast();

  // The place is known upfront when the wizard opens from a map resource, a map pin or a
  // reservation; the type picker is then restricted to what that place offers. The
  // resource is the finer filter of the two and the API takes only one, so it wins.
  // Opened from the queue nothing is known yet, so the office filter below seeds
  // itself from the filer's own default office instead of showing every type
  // in the organization. Falls back further to the session's active office
  // (currentOffice) for filers who have never set an explicit default one.
  const contextOfficeId =
    lockedOfficeId || resource?.officeId || mapPin?.officeId;
  const defaultUserOfficeId =
    authenticatedUser.user?.default_office_id ?? currentOffice?.id ?? undefined;

  // The type picker's own office scope — seeded from the opening context (pin/
  // resource/reservation) or the filer's default office, then user-editable.
  // Kept separate from contextOfficeId (which never changes after open) so
  // switching offices in the picker actually sticks.
  const [typeFilterOfficeId, setTypeFilterOfficeId] = useState<
    string | undefined
  >(() => contextOfficeId || defaultUserOfficeId);
  // Escape hatch when a search comes up empty in the scoped office: search
  // every office instead of dead-ending. officeId is omitted from the query
  // while this is on.
  const [isSearchingAllOfficesState, setIsSearchingAllOffices] =
    useState(false);
  // Widening to every office would surface types the gated resource does not
  // allow, so the escape hatch is closed rather than merely hidden.
  const isSearchingAllOffices = isGatedMode
    ? false
    : isSearchingAllOfficesState;
  const [pickedResource, setPickedResource] =
    useState<NewRequestResourceContext | null>(resource ?? null);

  // Location is now a ticket-level concept, so every request type can carry one.
  // resourceId reads pickedResource (state), not the resource prop — the API
  // drops officeId whenever resourceId is present, so once the user detaches
  // from the opening resource (or widens to every office) the stale prop
  // would otherwise keep pinning the query to it forever.
  const types = useSubmittableWorkplaceRequestTypes({
    resourceId: isSearchingAllOffices ? undefined : pickedResource?.id,
    officeId: isSearchingAllOffices ? undefined : typeFilterOfficeId
  });
  const categories = useWorkplaceRequestCategories();
  const offices = useOfficesQuery();
  const { createWorkplaceRequest } = useWorkplaceRequestMutations();

  const categoriesById = useMemo(
    () => new Map(categories.allElements.map((c) => [c.id, c])),
    [categories.allElements]
  );

  // When editing a draft, skip the type picker and start on the form step.
  const [step, setStep] = useState(initialDraft ? 1 : 0);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(
    initialDraft?.type.id ?? null
  );
  const [createdRequest, setCreatedRequest] =
    useState<WorkplaceRequestElement | null>(null);
  const isSubmittingRef = useRef(false);

  // Ticket-level location (resource / floor / pinned point). Seeded from the
  // opening context — a pinned map point, a picked desk/room, or the location of
  // the draft being edited — and thereafter edited from the sidebar picker.
  const locationFromContext = (): WorkplaceRequestLocationValue | null => {
    if (initialDraft?.location) return initialDraft.location;
    if (mapPin?.floorId) {
      return {
        type: "coordinates",
        floor_id: mapPin.floorId,
        x: mapPin.x,
        y: mapPin.y
      };
    }
    if (resource) return { type: "resource", resource_id: resource.id };
    return null;
  };
  const [location, setLocation] =
    useState<WorkplaceRequestLocationValue | null>(locationFromContext);
  // The resource behind a location picked in the finder. `location` keeps
  // only its id, and the reservation field needs its name, office and type
  // to show the resource's day.
  const [finderResource, setFinderResource] =
    useState<NewRequestResourceContext | null>(null);
  // True once the user has confirmed switching offices away from the pin/
  // resource the wizard opened with — unlocks the location field (it was
  // read-only while it mirrored that opening context) so a fresh one can be
  // picked for the new office.
  const [isLocationDetached, setIsLocationDetached] = useState(false);
  // Office change requested while a location is attached — switching would
  // orphan it, so it's held here for confirmation rather than applied
  // immediately. Null when no switch is pending.
  const [pendingOfficeId, setPendingOfficeId] = useState<string | null>(null);
  // Bumped whenever a pending office switch is dismissed, to force the
  // (uncontrolled) office selector to remount — typeFilterOfficeId itself
  // doesn't change on dismiss, but the selector has already optimistically
  // shown the rejected pick and needs to be told to revert some other way.
  const [officeSelectResetKey, setOfficeSelectResetKey] = useState(0);
  // Ensures an edited draft's saved values are loaded into the form exactly once,
  // so re-renders (e.g. offices loading) don't clobber the user's in-progress edits.
  const hasHydratedDraftRef = useRef(false);

  // Sync the picked resource + location when the wizard (re)opens, or the source
  // resource/pin changes.
  useEffect(() => {
    if (isOpen) {
      setPickedResource(resource ?? null);
      setLocation(locationFromContext());
      setFinderResource(null);
      setTypeFilterOfficeId(contextOfficeId || defaultUserOfficeId);
      setIsSearchingAllOffices(false);
      setIsLocationDetached(false);
      setPendingOfficeId(null);
    }
  }, [isOpen, resource?.id, mapPin?.floorId, mapPin?.x, mapPin?.y]);

  // A location attached elsewhere (the opening pin/resource, or one picked
  // from the sidebar) always wins over the picker's office filter — asks
  // before dropping it rather than letting the two silently disagree.
  const handleOfficeFilterChange = (newOfficeId: string) => {
    if (newOfficeId === typeFilterOfficeId) return;
    if (location) {
      setPendingOfficeId(newOfficeId);
      return;
    }
    setTypeFilterOfficeId(newOfficeId);
    setIsSearchingAllOffices(false);
    // The previous pick may not exist under the new office's list — drop it
    // rather than leave Continue enabled for a type that's since resolved to
    // nothing (step 2 would render blank).
    setSelectedTypeId(null);
  };

  const confirmOfficeSwitch = () => {
    if (!pendingOfficeId) return;
    setTypeFilterOfficeId(pendingOfficeId);
    setIsSearchingAllOffices(false);
    setLocation(null);
    setPickedResource(null);
    setIsLocationDetached(true);
    setSelectedTypeId(null);
    setPendingOfficeId(null);
  };

  const handleSelectType = (id: string) => {
    const type = pickerTypes.find((t) => t.id === id);
    // A cross-office search result may belong to one or more specific
    // offices — when the current filter isn't among them, follow it to the
    // first one so the picker (and step 2's office default) resolve the
    // type that was actually picked, instead of it vanishing once the list
    // re-scopes to the old office.
    if (
      isSearchingAllOffices &&
      type?.has_office_restrictions &&
      type.office_ids?.length &&
      !type.office_ids.includes(typeFilterOfficeId ?? "")
    ) {
      setTypeFilterOfficeId(type.office_ids[0]);
      // That office may not be where the currently attached location lives
      // (the opening pin/resource, or one picked from the sidebar) — drop
      // it rather than file the request under the new office while still
      // pointing at the old one's location.
      if (location) {
        setLocation(null);
        setPickedResource(null);
        setIsLocationDetached(true);
      }
    }
    setIsSearchingAllOffices(false);
    // Selecting a type advances to the form step — no separate Continue.
    setSelectedTypeId(id);
    setStep(1);
  };

  const resourceOfficeName = useMemo(
    () =>
      (offices.data?.offices ?? []).find(
        (o) => o.id === pickedResource?.officeId
      )?.name,
    [offices.data, pickedResource?.officeId]
  );

  // The resource the request is filed against, whichever way it got there:
  // the one the wizard opened with, or one picked in the location finder.
  // It follows the location, so changing or removing the location changes
  // what the reservation field shows. A restored draft carries only the
  // location's id and, with no resource to match it, shows nothing.
  const reservationResource = useMemo((): ReservationResourceContext | null => {
    if (location?.type !== "resource") return null;
    const candidate = [finderResource, pickedResource].find(
      (known) => known?.id === location.resource_id
    );
    if (!candidate) return null;
    const timeZone = offices.data?.byId[candidate.officeId]?.time_zone;
    if (!timeZone) return null;
    return {
      id: candidate.id,
      name: candidate.name,
      officeId: candidate.officeId,
      timeZone
    };
  }, [offices.data, location, finderResource, pickedResource]);

  const form = useForm<FieldValues>({ defaultValues: {} });
  const watchedOfficeId = form.watch("office_id") as string | undefined;
  const watchedOfficeName = useMemo(
    () =>
      (offices.data?.offices ?? []).find((o) => o.id === watchedOfficeId)?.name,
    [offices.data, watchedOfficeId]
  );
  const typeFilterOfficeName = useMemo(
    () =>
      (offices.data?.offices ?? []).find((o) => o.id === typeFilterOfficeId)
        ?.name,
    [offices.data, typeFilterOfficeId]
  );

  const selectedType = useMemo(() => {
    const found = types.allElements.find((t) => t.id === selectedTypeId);
    if (found) return found;
    // Fall back to the draft's type so editing a saved request still renders even
    // if its type is missing from the submittable list (e.g. later restricted).
    if (initialDraft && initialDraft.type.id === selectedTypeId) {
      return initialDraft.type;
    }
    return null;
  }, [types.allElements, selectedTypeId, initialDraft]);

  // A type can defer "requires a workspace" to approval time (see the same
  // gate in Sidebar.tsx) — only when it doesn't should the wizard demand a
  // real desk/room up front, rather than letting a floor or dropped pin through.
  // The deferral only means something when there's an approval step to defer
  // to — without `requires_approval`, `require_location_at_approval` is a
  // stale leftover (the type may have had approval turned off after it was
  // set) and must not be trusted to skip the workspace requirement.
  const requiresWorkspaceAtCreation =
    !!selectedType?.requires_location &&
    !(
      selectedType?.require_location_at_approval &&
      selectedType?.requires_approval
    );

  const officeOptions = useMemo(() => {
    const all = (offices.data?.offices ?? []).map((o) => ({
      id: o.id,
      name: o.name
    }));
    if (
      selectedType?.has_office_restrictions &&
      selectedType.office_ids?.length
    ) {
      const allowed = new Set(selectedType.office_ids);
      return all.filter((o) => allowed.has(o.id));
    }
    return all;
  }, [offices.data, selectedType]);

  // Any request type can carry a ticket-level location, so the picker is never
  // filtered by the presence of a location field. Collect mode is the one
  // exception: it attaches requests to a reservation, and a type that produces
  // nothing has nothing to attach.
  const modeFilteredTypes = isCollectMode
    ? types.allElements.filter(isSubmittableRequestType)
    : types.allElements;
  // A type that requires a reservation can only be used alongside one — hide
  // it unless the wizard already has (collect mode) or is filing against
  // (scheduleId) one. Otherwise the picker offers a type the submission has
  // no way to satisfy.
  const hasScheduleContext = isCollectMode || !!scheduleId;
  const scheduleFilteredTypes = hasScheduleContext
    ? modeFilteredTypes
    : modeFilteredTypes.filter(
        (type) => type.schedule_requirement !== "required"
      );
  // A gated resource is bookable only through the types configured on it.
  const pickerTypes = allowedTypeIds?.length
    ? scheduleFilteredTypes.filter((type) => allowedTypeIds.includes(type.id))
    : scheduleFilteredTypes;

  const isInstructionsOnly =
    !!selectedType && !isSubmittableRequestType(selectedType);

  // A gated resource offering exactly one type leaves nothing to pick, so the
  // wizard opens on the form — the same shortcut editing a draft takes. The ref
  // keeps Back from bouncing straight forward again.
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasAutoSelectedRef.current = false;
      return;
    }
    if (
      !isGatedMode ||
      hasAutoSelectedRef.current ||
      selectedTypeId ||
      types.isFetching ||
      pickerTypes.length !== 1
    ) {
      return;
    }
    hasAutoSelectedRef.current = true;
    setSelectedTypeId(pickerTypes[0].id);
    setStep(1);
  }, [isOpen, isGatedMode, selectedTypeId, types.isFetching, pickerTypes]);

  // Reset the dynamic form whenever the selected type (or office list) changes.
  useEffect(() => {
    if (!selectedType) return;
    // Editing an existing draft: load its saved values once and keep them.
    if (initialDraft && selectedType.id === initialDraft.type.id) {
      if (!hasHydratedDraftRef.current) {
        form.reset(initialDraft.values);
        hasHydratedDraftRef.current = true;
      }
      return;
    }
    const defaultOffice =
      lockedOfficeId ||
      // The type picker's own filter — reflects an explicit office switch,
      // so it must outrank the (unchanged, opener-provided) resource/pin
      // props below once the user has moved past them.
      (typeFilterOfficeId &&
        officeOptions.find((o) => o.id === typeFilterOfficeId)?.id) ||
      (pickedResource &&
        officeOptions.find((o) => o.id === pickedResource.officeId)?.id) ||
      (mapPin && officeOptions.find((o) => o.id === mapPin.officeId)?.id) ||
      officeOptions.find(
        (o) => o.id === authenticatedUser.user?.default_office_id
      )?.id ||
      officeOptions[0]?.id ||
      "";
    // `title` and `description` are first-class on the request ticket (not form
    // fields), so they always appear in the form and map to the ticket directly.
    const defaults: FieldValues = {
      office_id: defaultOffice,
      title: "",
      description: ""
    };
    for (const field of selectedType.fields ?? []) {
      if (!isSubmittableFieldType(field.field_type)) {
        continue;
      }
      if (field.field_type === "reservation" && reservationWhen) {
        defaults[field.id] = reservationValueFromWhen(
          reservationWhen,
          reservationFieldConfig(field)
        );
        continue;
      }
      defaults[field.id] =
        field.field_type === "toggle"
          ? false
          : field.field_type === "multi_select"
            ? []
            : "";
    }
    form.reset(defaults);
  }, [selectedType?.id, offices.data]);

  // Keep the form's office in sync with a picker office change made after the
  // type was already chosen (e.g. Back, switch office, Continue with the same
  // type) — the reset effect above only re-derives it when the type itself
  // (or the office list) changes, so without this "Filing this for" would
  // keep showing the office the type was first picked under. A targeted
  // setValue rather than another form.reset, so the rest of what's been
  // filled in isn't wiped out by an office-only change.
  useEffect(() => {
    if (!selectedType || lockedOfficeId || !typeFilterOfficeId) return;
    const validOffice = officeOptions.find(
      (o) => o.id === typeFilterOfficeId
    )?.id;
    if (validOffice && validOffice !== form.getValues("office_id")) {
      form.setValue("office_id", validOffice);
    }
  }, [typeFilterOfficeId]);

  const reset = () => {
    setStep(0);
    setSelectedTypeId(null);
    setCreatedRequest(null);
    hasHydratedDraftRef.current = false;
    // Restore the prop-provided context so "Submit another" (which keeps the
    // wizard open) starts from the same resource/pin the wizard was opened with.
    setPickedResource(resource ?? null);
    setLocation(locationFromContext());
    setTypeFilterOfficeId(contextOfficeId || defaultUserOfficeId);
    setIsSearchingAllOffices(false);
    setIsLocationDetached(false);
    setPendingOfficeId(null);
  };

  const handleClose = (open: boolean) => {
    onOpenChange(open);
    if (!open) reset();
  };

  // Collect mode: validate and hand the assembled draft back to the caller instead
  // of creating a ticket. The reservation flow persists it when the reservation is saved.
  const collect = async (): Promise<void> => {
    const ok = await form.trigger();
    if (!ok || !selectedType || !onCollect) {
      return;
    }
    if (requiresWorkspaceAtCreation && location?.type !== "resource") {
      toast.error("Pick a desk or room before continuing.");
      return;
    }
    const values = form.getValues();
    const title = String(values.title || "").trim() || selectedType.name;
    const description = descriptionFor(selectedType, values);
    const office_id = lockedOfficeId || String(values.office_id || "");
    onCollect({
      draftId: initialDraft?.draftId ?? "",
      type: selectedType,
      title,
      description: description || undefined,
      office_id,
      location: location ?? undefined,
      field_values: buildFieldValues(selectedType, values),
      values: { ...values, office_id }
    });
    handleClose(false);
  };

  const submit = async (): Promise<boolean> => {
    // Belt and braces: the wizard already ignores clicks while advancing, but
    // this is the call that creates the ticket, so it must never run twice for
    // one request regardless of who invokes it.
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;
    try {
      return await submitOnce();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const submitOnce = async (): Promise<boolean> => {
    const ok = await form.trigger();
    if (!ok || !selectedType) {
      return false;
    }
    if (requiresWorkspaceAtCreation && location?.type !== "resource") {
      toast.error("Pick a desk or room before submitting.");
      return false;
    }
    const values = form.getValues();
    const title = String(values.title || "").trim() || selectedType.name;
    const description = descriptionFor(selectedType, values);
    try {
      const created = await createWorkplaceRequest({
        type_id: selectedType.id,
        office_id: String(values.office_id || ""),
        status: "open",
        title,
        description: description || undefined,
        location: location ?? undefined,
        schedule_id: scheduleId,
        field_values: buildFieldValues(selectedType, values)
      });
      setCreatedRequest(created ?? null);
      return true;
    } catch {
      return false;
    }
  };

  const steps: WizardStep[] = [
    {
      id: "type",
      label: "Pick a request type",
      description: "Choose the request type that best matches your need.",
      content: (
        <RequestTypePicker
          types={pickerTypes}
          categoriesById={categoriesById}
          selectedTypeId={selectedTypeId}
          onSelect={handleSelectType}
          officeId={typeFilterOfficeId}
          officeName={typeFilterOfficeName}
          onOfficeChange={handleOfficeFilterChange}
          officeSelectorDisabled={!!lockedOfficeId || isGatedMode}
          isSearchingAllOffices={isSearchingAllOffices}
          onSearchAllOffices={() => setIsSearchingAllOffices(true)}
          isLoadingTypes={types.isFetching}
          officeSelectResetKey={officeSelectResetKey}
        />
      ),
      validate: () => !!selectedTypeId
    },
    // A link type replaces the form outright. Because this branch carries no
    // `validate`, `submit` is unreachable rather than merely hidden.
    isInstructionsOnly && selectedType
      ? {
          id: "instructions",
          label: "Follow the instructions",
          description:
            "This request is filed outside Tactic — nothing will be tracked here.",
          content: (
            <RequestInstructionsStep
              type={selectedType}
              category={categoriesById.get(selectedType.category_id)}
              onChangeType={() => setStep(0)}
            />
          )
        }
      : {
          id: "form",
          label: "Fill out the form",
          description: "Provide the details for your request.",
          content: selectedType ? (
            <RequestFormStep
              form={form}
              type={selectedType}
              category={categoriesById.get(selectedType.category_id)}
              offices={officeOptions}
              officeDisabled={!!lockedOfficeId}
              onChangeType={() => setStep(0)}
              location={location}
              submitterId={authenticatedUser.user?.id ?? null}
              reservationResource={reservationResource}
            />
          ) : null,
          // In collect mode the footer drives collection directly, so no step validation.
          validate: isCollectMode ? undefined : submit
        },
    // The confirmation step only applies to the save flow; collect mode closes on
    // add, and a link type has nothing to confirm.
    ...(isCollectMode || isInstructionsOnly
      ? []
      : [
          {
            id: "confirm",
            label: "Confirmation",
            description: "Review your submitted request.",
            content: createdRequest ? (
              <RequestConfirmation
                request={createdRequest}
                submittedByName={viewerName}
                categoryName={
                  categoriesById.get(selectedType?.category_id ?? "")?.name
                }
                locationLabel={
                  [
                    pickedResource?.name,
                    (offices.data?.offices ?? []).find(
                      (o) => o.id === createdRequest.office_id
                    )?.name,
                    pickedResource?.floorName
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
              />
            ) : null
          }
        ])
  ];

  const renderFooter = (props: WizardFooterRenderProps): React.ReactNode => {
    if (props.currentStep === 0) {
      return (
        <div className="flex w-full items-center justify-between">
          <Button variant="ghost" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            {/* Says where Continue leads, so nobody is surprised by a step that
                dead-ends. */}
            {isInstructionsOnly ? (
              <span className="text-neutral-500 text-xs">
                Filed outside Tactic
              </span>
            ) : null}
            <Button
              onClick={props.goNext}
              disabled={!selectedTypeId}
              iconRight={<ChevronRight className="h-4 w-4" />}
            >
              Continue
            </Button>
          </div>
        </div>
      );
    }
    if (props.currentStep === 1) {
      return (
        <div className="flex w-full items-center justify-between">
          <Button
            variant="ghost"
            onClick={props.goBack}
            iconLeft={<ChevronLeft className="h-4 w-4" />}
          >
            Back
          </Button>
          {isInstructionsOnly ? (
            // No primary "Open ServiceNow": a free-form body has no canonical
            // URL, and promoting the first link would make a formatting choice
            // load-bearing.
            <Button variant="outline" onClick={() => handleClose(false)}>
              Close
            </Button>
          ) : isCollectMode ? (
            <Button onClick={collect}>
              {isEditMode ? "Save request" : "Add to reservation"}
            </Button>
          ) : (
            <Button onClick={props.goNext} isLoading={props.isLoading}>
              Submit request
            </Button>
          )}
        </div>
      );
    }
    return (
      <div className="flex w-full items-center justify-between">
        <Button variant="ghost" onClick={reset}>
          Submit another request
        </Button>
        <Button
          onClick={() => {
            handleClose(false);
            onViewQueue?.();
          }}
          iconRight={<ChevronRight className="h-4 w-4" />}
        >
          Done
        </Button>
      </div>
    );
  };

  const pendingOfficeName = (offices.data?.offices ?? []).find(
    (o) => o.id === pendingOfficeId
  )?.name;

  return (
    <>
      <WizardModal
        isOpen={isOpen}
        onOpenChange={handleClose}
        confirmOnClose
        contained={contained}
        portalContainer={portalContainer}
        title={
          isCollectMode
            ? "Add a request to the reservation"
            : "Submit a request"
        }
        description={`Filing as ${viewerName}`}
        steps={steps}
        currentStep={step}
        onStepChange={setStep}
        size="4xl"
        bodyHeight={{
          height: "80vh",
          maxHeight: "calc(100vh - 200px)",
          minHeight: "480px"
        }}
        renderFooter={renderFooter}
        sidebarFooter={
          <RequestLocationField
            value={location}
            onChange={setLocation}
            onResourcePicked={(picked) =>
              setFinderResource({
                id: picked.id,
                name: picked.label || picked.name,
                officeId: picked.office_id,
                type: picked.type
              })
            }
            // Fall back to the type picker's office filter, then the opening map
            // context, so the pin/resource preview — and a freshly picked
            // location after an office switch — render in the right office
            // even before a request type (and its own office default) is picked.
            officeId={
              watchedOfficeId ||
              typeFilterOfficeId ||
              mapPin?.officeId ||
              pickedResource?.officeId
            }
            officeName={
              watchedOfficeName ??
              typeFilterOfficeName ??
              mapPin?.officeName ??
              resourceOfficeName
            }
            prefilledResource={
              pickedResource
                ? {
                    id: pickedResource.id,
                    name: pickedResource.name,
                    floorName: pickedResource.floorName
                  }
                : undefined
            }
            // Filed from the map against a fixed resource / pin → read-only
            // preview, unless the user has since switched offices and dropped it.
            locked={(!!resource || !!mapPin) && !isLocationDetached}
            requiresWorkspace={requiresWorkspaceAtCreation}
          />
        }
      />
      <ConfirmModal
        isOpen={!!pendingOfficeId}
        onOpenChange={(open) => {
          if (open) return;
          setPendingOfficeId(null);
          // The office selector already optimistically showed the rejected
          // office — force it to remount and revert to typeFilterOfficeId.
          setOfficeSelectResetKey((key) => key + 1);
        }}
        onConfirm={confirmOfficeSwitch}
        title="Switch office?"
        message={
          <>
            Changing the office to {pendingOfficeName || "this office"} will
            remove the location you attached. You can pick a new one for the new
            office afterward.
          </>
        }
        confirmLabel="Switch office"
      />
    </>
  );
};
